import { useEffect, useState } from "react";
import { Accordion, Badge, Box, Text, Tooltip } from "@mantine/core";
import {
  IconChartLine,
  IconSettings,
  IconBell,
  IconRobot,
  IconList,
  IconLayout2,
} from "@tabler/icons-react";
import { getWidgetsByCategory } from "./registry";
import { WidgetDefinition } from "@pulse-ui/widget-sdk";
import { IconGripVertical } from "@tabler/icons-react";

const WIDGET_DRAG_MIME_TYPE = "application/x-atlas-widget";

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Trading: IconChartLine,
  Management: IconSettings,
  Alerts: IconBell,
  Algo: IconRobot,
  Blotter: IconList,
};

function getCategoryIcon(category: string): React.ElementType {
  return CATEGORY_ICONS[category] ?? IconLayout2;
}

interface WidgetLibraryProps {
  widgetRegistry?: Record<string, WidgetDefinition>;
  onWidgetSelect?: (widgetId: string) => void;
  /** When set, this category is opened and all others are closed */
  focusCategory?: string | null;
  /** Called when user clicks an icon in collapsed sidebar mode */
  onCategoryIconClick?: (category: string) => void;
  /** Whether the sidebar is collapsed (icon-only mode) */
  collapsed?: boolean;
}

export function WidgetLibrary({
  widgetRegistry,
  onWidgetSelect,
  focusCategory,
  onCategoryIconClick,
  collapsed = false,
}: WidgetLibraryProps) {
  const widgetsByCategory = widgetRegistry
    ? groupWidgetsByCategory(widgetRegistry)
    : getWidgetsByCategory();

  const categories = Object.keys(widgetsByCategory);

  const [openCategory, setOpenCategory] = useState<string | null>(
    categories[0] ?? null,
  );

  // Sync external focusCategory into local state when it changes
  useEffect(() => {
    if (focusCategory != null) setOpenCategory(focusCategory);
  }, [focusCategory]);

  const handleDragStart = (e: React.DragEvent, widget: WidgetDefinition) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(WIDGET_DRAG_MIME_TYPE, widget.id);
    e.dataTransfer.setData("text/plain", widget.name);
  };

  if (categories.length === 0) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">No widgets available.</Text>
      </Box>
    );
  }

  // Collapsed: show only category icons stacked vertically
  if (collapsed) {
    return (
      <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, paddingTop: 8 }}>
        {categories.map((category) => {
          const Icon = getCategoryIcon(category);
          return (
            <Tooltip key={category} label={category} position="right" withArrow>
              <Box
                onClick={() => onCategoryIconClick?.(category)}
                style={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "var(--mantine-radius-sm)",
                  cursor: "pointer",
                  color: "var(--mantine-color-dimmed)",
                  transition: "background-color 120ms ease, color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.backgroundColor = "var(--mantine-color-default)";
                  el.style.color = "var(--mantine-color-text)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.backgroundColor = "transparent";
                  el.style.color = "var(--mantine-color-dimmed)";
                }}
              >
                <Icon size={18} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  // Expanded: accordion with single-open category
  return (
    <Accordion
      value={openCategory}
      onChange={(val) => setOpenCategory(val)}
      chevronPosition="right"
      styles={{
        root: { padding: "4px 0" },
        item: { border: "none" },
        control: {
          padding: "6px 12px",
          "&:hover": { backgroundColor: "var(--mantine-color-default)" },
        },
        label: { padding: 0 },
        content: { padding: "0 0 4px 0" },
        chevron: { color: "var(--mantine-color-dimmed)" },
      }}
    >
      {categories.map((category) => {
        const Icon = getCategoryIcon(category);
        return (
          <Accordion.Item key={category} value={category}>
            <Accordion.Control icon={<Icon size={14} style={{ color: "var(--mantine-color-dimmed)" }} />}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>
                {category}
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Box style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {widgetsByCategory[category].map((widget) => (
                  <Tooltip
                    key={widget.id}
                    label="Coming soon"
                    disabled={!widget.disabled}
                    position="right"
                    withArrow
                  >
                    <Box
                      draggable={!widget.disabled}
                      onDragStart={widget.disabled ? undefined : (e) => handleDragStart(e, widget)}
                      onClick={widget.disabled ? undefined : () => onWidgetSelect?.(widget.id)}
                      px="xs"
                      py={6}
                      style={{
                        cursor: widget.disabled ? "default" : "grab",
                        borderRadius: "var(--mantine-radius-sm)",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        transition: "background-color 120ms ease",
                        opacity: widget.disabled ? 0.45 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (widget.disabled) return;
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "var(--mantine-color-default)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                      }}
                    >
                      <IconGripVertical
                        size={14}
                        style={{ marginTop: 3, flexShrink: 0, color: "var(--mantine-color-dimmed)" }}
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate c="var(--mantine-color-text)">
                          {widget.name}
                          {widget.disabled && (
                            <Badge size="xs" variant="outline" color="gray" ml={6} style={{ verticalAlign: "middle" }}>
                              Soon
                            </Badge>
                          )}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {widget.description}
                        </Text>
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

function groupWidgetsByCategory(
  registry: Record<string, WidgetDefinition>,
): Record<string, WidgetDefinition[]> {
  const byCategory: Record<string, WidgetDefinition[]> = {};
  Object.values(registry).forEach((widget) => {
    if (!byCategory[widget.category]) byCategory[widget.category] = [];
    byCategory[widget.category].push(widget);
  });
  return byCategory;
}
